import React, { useState, useEffect } from 'react';
import { AdminLayout } from './AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { getAdminPartners, createPartner, updatePartner, deletePartner } from '../../api/client';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, GripVertical, ExternalLink, Upload, Eye } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = ['Government', 'Associations', 'Media', 'Corporate', 'NGO', 'Other'];

export const AdminPartners = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    logo: '',
    category: 'Government',
    url: '',
    order: 0,
    visible: true
  });

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    try {
      const data = await getAdminPartners();
      setPartners(data.partners || []);
    } catch (error) {
      toast.error('Failed to load partners');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File must be less than 5MB');
      return;
    }

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const token = sessionStorage.getItem('adminToken');
      const response = await axios.post(`${BACKEND_URL}/api/admin/upload`, formDataUpload, {
        headers: { 
          'Content-Type': 'multipart/form-data', 
          'Authorization': `Bearer ${token}` 
        }
      });
      const fileUrl = `${BACKEND_URL}${response.data.url}`;
      setFormData({ ...formData, logo: fileUrl });
      toast.success('Logo uploaded!');
    } catch (error) {
      toast.error('Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const openDialog = (partner = null) => {
    if (partner) {
      setEditingPartner(partner);
      setFormData({
        name: partner.name || '',
        logo: partner.logo || '',
        category: partner.category || 'Government',
        url: partner.url || '',
        order: partner.order || 0,
        visible: partner.visible !== false
      });
    } else {
      setEditingPartner(null);
      setFormData({
        name: '',
        logo: '',
        category: 'Government',
        url: '',
        order: partners.length,
        visible: true
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Partner name is required');
      return;
    }

    try {
      if (editingPartner) {
        await updatePartner(editingPartner.id, formData);
        toast.success('Partner updated!');
      } else {
        await createPartner(formData);
        toast.success('Partner created!');
      }
      setDialogOpen(false);
      fetchPartners();
    } catch (error) {
      toast.error('Failed to save partner');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this partner?')) return;
    
    try {
      await deletePartner(id);
      toast.success('Partner deleted!');
      fetchPartners();
    } catch (error) {
      toast.error('Failed to delete partner');
    }
  };

  const toggleVisibility = async (partner) => {
    try {
      await updatePartner(partner.id, { ...partner, visible: !partner.visible });
      fetchPartners();
      toast.success(partner.visible ? 'Partner hidden' : 'Partner visible');
    } catch (error) {
      toast.error('Failed to update visibility');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin h-8 w-8 border-4 border-green-600 border-t-transparent rounded-full"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">Partners</h2>
            <p className="text-gray-600 mt-1">Manage partner logos and information</p>
          </div>
          <Button onClick={() => openDialog()} className="bg-green-600 hover:bg-green-700 flex items-center gap-2">
            <Plus size={16} />
            Add Partner
          </Button>
        </div>

        {partners.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-gray-500 mb-4">No partners added yet</p>
              <Button onClick={() => openDialog()} variant="outline">
                <Plus size={16} className="mr-2" />
                Add your first partner
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {partners.map((partner, index) => (
              <Card key={partner.id} className={`${!partner.visible ? 'opacity-60' : ''}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="text-gray-400 cursor-move">
                      <GripVertical size={20} />
                    </div>
                    
                    <div className="w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
                      {partner.logo ? (
                        <img src={partner.logo} alt={partner.name} className="w-full h-full object-contain p-2" />
                      ) : (
                        <span className="text-gray-400 text-xs">No logo</span>
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900">{partner.name}</h3>
                      <p className="text-sm text-gray-500">{partner.category}</p>
                      {partner.url && (
                        <a href={partner.url} target="_blank" rel="noopener noreferrer" className="text-sm text-green-600 hover:underline flex items-center gap-1">
                          <ExternalLink size={12} />
                          Website
                        </a>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleVisibility(partner)}
                        title={partner.visible ? 'Hide' : 'Show'}
                      >
                        <Eye size={16} className={partner.visible ? 'text-green-600' : 'text-gray-400'} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openDialog(partner)}>
                        <Edit2 size={16} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(partner.id)} className="text-red-600 hover:text-red-700">
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{editingPartner ? 'Edit Partner' : 'Add Partner'}</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <Label>Partner Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Partner Organization Name"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>Logo</Label>
                <div className="mt-1 space-y-2">
                  <label className="block">
                    <div className={`
                      flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed 
                      rounded-lg cursor-pointer transition-colors
                      ${uploading ? 'border-gray-300 bg-gray-50' : 'border-gray-300 hover:border-green-500 hover:bg-green-50'}
                    `}>
                      {uploading ? (
                        <>
                          <div className="animate-spin h-4 w-4 border-2 border-green-600 border-t-transparent rounded-full"></div>
                          <span className="text-sm text-gray-500">Uploading...</span>
                        </>
                      ) : (
                        <>
                          <Upload size={16} className="text-gray-500" />
                          <span className="text-sm text-gray-600">Upload Logo</span>
                        </>
                      )}
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileUpload}
                      disabled={uploading}
                      className="hidden"
                    />
                  </label>
                  {formData.logo && (
                    <div className="w-24 h-24 bg-gray-100 rounded-lg overflow-hidden">
                      <img src={formData.logo} alt="Logo preview" className="w-full h-full object-contain p-2" />
                    </div>
                  )}
                </div>
              </div>
              
              <div>
                <Label>Category</Label>
                <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Website URL</Label>
                <Input
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  placeholder="https://partner-website.com"
                  className="mt-1"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <Label>Visible on website</Label>
                <Switch
                  checked={formData.visible}
                  onCheckedChange={(checked) => setFormData({ ...formData, visible: checked })}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} className="bg-green-600 hover:bg-green-700">
                {editingPartner ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};
